import React, { useState, useRef, useEffect, useCallback } from "react";
import LyricsDisplay from "./LyricsDisplay";

// ── LRC parser ────────────────────────────────────────────────
function parseLRC(lrcText) {
  const lines = lrcText.split("\n");
  const result = [];
  for (const line of lines) {
    // Match [mm:ss.xx] or [mm:ss]
    const match = line.match(/^\[(\d{2}):(\d{2})(?:\.(\d{1,3}))?\](.*)$/);
    if (match) {
      const min = parseInt(match[1], 10);
      const sec = parseInt(match[2], 10);
      const ms = match[3] ? parseInt(match[3].padEnd(3, "0"), 10) : 0;
      const time = min * 60 + sec + ms / 1000;
      const text = match[4].trim();
      if (text) {
        result.push({ time, text });
      }
    }
  }
  return result.sort((a, b) => a.time - b.time);
}

// ── Markdown lyrics parser ────────────────────────────────────
function parseMarkdownLyrics(mdText) {
  const idx = mdText.indexOf("## Lyrics");
  if (idx === -1) return [];
  const lyricsSection = mdText.substring(idx + "## Lyrics".length);
  return lyricsSection.split("\n");
}

// ── Format seconds to mm:ss ───────────────────────────────────
function formatTime(sec) {
  if (isNaN(sec)) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

// ── SVG Icons ─────────────────────────────────────────────────
const PlayIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22">
    <path d="M8 5v14l11-7z" fill="currentColor" />
  </svg>
);
const PauseIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22">
    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" fill="currentColor" />
  </svg>
);
const VolumeIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" />
    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
  </svg>
);
const SkipBackIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
    <path d="M6 6h2v12H6V6zm3.5 6 8.5 6V6l-8.5 6z" />
  </svg>
);
const SkipForwardIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
    <path d="M16 18h2V6h-2v12zM6 18l8.5-6L6 6v12z" />
  </svg>
);

const SongPlayer = ({ song, onBack }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [isKaraoke, setIsKaraoke] = useState(false);
  const [karaokeAvailable, setKaraokeAvailable] = useState(false);
  const [lyricsMode, setLyricsMode] = useState("static"); // "static" | "timed"
  const [staticLines, setStaticLines] = useState([]);
  const [timedLines, setTimedLines] = useState([]);
  const [lyricsLoading, setLyricsLoading] = useState(true);

  // ── Check if karaoke file exists ────────────────────────
  useEffect(() => {
    if (!song.karaokeUrl) {
      setKaraokeAvailable(false);
      return;
    }
    fetch(song.karaokeUrl, { method: "HEAD" })
      .then((res) => setKaraokeAvailable(res.ok))
      .catch(() => setKaraokeAvailable(false));
  }, [song.karaokeUrl]);

  // ── Load static lyrics from markdown ────────────────────
  useEffect(() => {
    setLyricsLoading(true);
    fetch(`/songs/${song.lyricsFile}`)
      .then((res) => res.text())
      .then((text) => {
        setStaticLines(parseMarkdownLyrics(text));
      })
      .catch(() => setStaticLines([]))
      .finally(() => setLyricsLoading(false));
  }, [song.lyricsFile]);

  // ── Load timed lyrics from LRC ──────────────────────────
  useEffect(() => {
    if (!song.timedLyricsFile) {
      setTimedLines([]);
      return;
    }
    const url = `/songs/timed-lyrics/${encodeURIComponent(song.timedLyricsFile)}`;
    fetch(url)
      .then((res) => res.text())
      .then((text) => {
        setTimedLines(parseLRC(text));
      })
      .catch(() => setTimedLines([]));
  }, [song.timedLyricsFile]);

  // ── Audio event handlers ────────────────────────────────
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoadedMetadata = () => setDuration(audio.duration);
    const onEnded = () => setIsPlaying(false);

    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("ended", onEnded);

    return () => {
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("ended", onEnded);
    };
  }, []);

  // ── Play / Pause ────────────────────────────────────────
  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch(() => {});
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying]);

  // ── Seek ────────────────────────────────────────────────
  const handleSeek = useCallback((e) => {
    const audio = audioRef.current;
    if (!audio) return;
    const newTime = parseFloat(e.target.value);
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  }, []);

  const seekTo = useCallback((time) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = time;
    setCurrentTime(time);
    if (!isPlaying) {
      audio.play().catch(() => {});
      setIsPlaying(true);
    }
  }, [isPlaying]);

  // ── Skip ────────────────────────────────────────────────
  const skip = useCallback((delta) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.max(0, Math.min(audio.duration || 0, audio.currentTime + delta));
    setCurrentTime(audio.currentTime);
  }, []);

  // ── Volume ──────────────────────────────────────────────
  const handleVolume = useCallback((e) => {
    const v = parseFloat(e.target.value);
    setVolume(v);
    if (audioRef.current) audioRef.current.volume = v;
  }, []);

  // ── Karaoke toggle ──────────────────────────────────────
  const toggleKaraoke = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const wasPlaying = !audio.paused;
    const time = audio.currentTime;
    const newIsKaraoke = !isKaraoke;
    setIsKaraoke(newIsKaraoke);

    const newSrc = newIsKaraoke ? song.karaokeUrl : song.audioUrl;
    audio.src = newSrc;
    audio.volume = volume;

    const onCanPlay = () => {
      audio.currentTime = time;
      if (wasPlaying) audio.play().catch(() => {});
      audio.removeEventListener("canplay", onCanPlay);
    };
    audio.addEventListener("canplay", onCanPlay);
    audio.load();
  }, [isKaraoke, song, volume]);

  // ── Seekbar gradient ────────────────────────────────────
  const seekPercent = duration ? (currentTime / duration) * 100 : 0;
  const seekStyle = {
    background: `linear-gradient(to right, #FCEE0C ${seekPercent}%, rgba(255,255,255,0.1) ${seekPercent}%)`,
  };

  return (
    <div className="player-view">
      <div className="player-layout">
        {/* ── Left: Artwork + Controls ─────────────────── */}
        <div className="player-artwork-panel">
          <div className="player-artwork-wrap">
            <img
              src={song.artworkUrl}
              alt={song.title}
              className="player-artwork"
            />
          </div>

          <h2 className="player-song-title">{song.title}</h2>
          <p className="player-artist">DeNyx</p>

          {/* Audio Controls */}
          <div className="audio-controls">
            <div className="audio-seekbar-row">
              <span className="audio-time">{formatTime(currentTime)}</span>
              <input
                type="range"
                className="audio-seekbar"
                min={0}
                max={duration || 0}
                step={0.1}
                value={currentTime}
                onChange={handleSeek}
                style={seekStyle}
              />
              <span className="audio-time">{formatTime(duration)}</span>
            </div>

            <div className="audio-buttons-row">
              <button className="audio-btn" onClick={() => skip(-10)} title="Back 10s">
                <SkipBackIcon />
              </button>
              <button className="audio-btn-play" onClick={togglePlay}>
                {isPlaying ? <PauseIcon /> : <PlayIcon />}
              </button>
              <button className="audio-btn" onClick={() => skip(10)} title="Forward 10s">
                <SkipForwardIcon />
              </button>
            </div>

            <div className="volume-control">
              <span className="volume-icon"><VolumeIcon /></span>
              <input
                type="range"
                className="volume-slider"
                min={0}
                max={1}
                step={0.01}
                value={volume}
                onChange={handleVolume}
              />
            </div>
          </div>

          {/* Toggles */}
          <div className="player-toggles">
            <button
              className={"toggle-btn" + (isKaraoke ? " active-pink" : "")}
              onClick={toggleKaraoke}
              disabled={!karaokeAvailable}
              title={karaokeAvailable ? "Switch to karaoke version" : "No karaoke version available"}
            >
              {isKaraoke ? "♪ Karaoke" : "♪ Normal"}
            </button>

            {song.timedLyricsFile && timedLines.length > 0 && (
              <>
                <button
                  className={"toggle-btn" + (lyricsMode === "static" ? " active" : "")}
                  onClick={() => setLyricsMode("static")}
                >
                  Static Lyrics
                </button>
                <button
                  className={"toggle-btn" + (lyricsMode === "timed" ? " active" : "")}
                  onClick={() => setLyricsMode("timed")}
                >
                  Timed Lyrics
                </button>
              </>
            )}
          </div>
        </div>

        {/* ── Right: Lyrics ────────────────────────────── */}
        <div>
          {lyricsLoading ? (
            <div className="music-loading">
              <div className="spinner" />
              Loading lyrics...
            </div>
          ) : (
            <LyricsDisplay
              mode={lyricsMode}
              staticLines={staticLines}
              timedLines={timedLines}
              currentTime={currentTime}
              onSeek={seekTo}
            />
          )}
        </div>
      </div>

      {/* Hidden audio element */}
      <audio ref={audioRef} src={song.audioUrl} preload="auto" />
    </div>
  );
};

export default SongPlayer;
